'use client';
import { AiOutlineUnlock } from 'react-icons/ai';
import clsx from 'clsx';

import { FilterField } from '../../_components/FilterField';
import { DATE_PRESETS, SORT_OPTIONS, ToolbarState } from './tradesQuery';

interface TradesToolbarProps {
  value: ToolbarState;
  disabled: boolean;
  onChange: (next: ToolbarState) => void;
  lockedTicker?: string;
  memberFilter?: string;
  onMemberFilterChange?: (value: string) => void;
  showTransaction?: boolean;
  hideChamber?: boolean;
  hideSort?: boolean;
}

export const TradesToolbar = ({
  disabled,
  onChange,
  value,
  lockedTicker,
  memberFilter = '',
  onMemberFilterChange,
  showTransaction = false,
  hideChamber = false,
  hideSort = false,
}: TradesToolbarProps) => {
  const set = (patch: Partial<ToolbarState>) => onChange({ ...value, ...patch });
  const tickerLocked = Boolean(lockedTicker);

  return (
    <div
      className={clsx(
        'relative flex flex-wrap items-center gap-2.5 rounded-t-[10px] border border-b-0 border-[#e3e9f4] bg-white px-4 py-3.5 max-sm:gap-2 max-sm:p-3',
        disabled && '[&_input]:cursor-not-allowed [&_label]:opacity-55 [&_select]:cursor-not-allowed',
      )}
    >
      <div className="group relative flex min-w-[240px] flex-1 items-center rounded-lg border border-[#e3e9f4] bg-[#f6f8fc] px-3 max-sm:w-full max-sm:min-w-0">
        {tickerLocked ? (
          <input
            className="w-full border-0 bg-transparent py-2.5 text-[13px] font-semibold text-bzgray-900 outline-none placeholder:text-bzgray-600 max-sm:text-base"
            disabled={disabled}
            onChange={event => onMemberFilterChange?.(event.target.value)}
            placeholder="Filter by member name"
            value={memberFilter}
          />
        ) : (
          <input
            className="w-full border-0 bg-transparent py-2.5 text-[13px] font-semibold text-bzgray-900 outline-none placeholder:text-bzgray-600 max-sm:text-base"
            disabled={disabled}
            onChange={event => set({ ticker: event.target.value.toUpperCase() })}
            placeholder="Search by ticker — e.g. MSFT"
            value={value.ticker}
          />
        )}
        {disabled && (
          <a
            className="absolute inset-0 z-[2] hidden items-center justify-center gap-1.5 rounded-lg bg-white/55 text-xs font-bold text-[#3f6df6] backdrop-blur-[2px] group-hover:flex"
            href="https://www.benzinga.com/premium/ideas/benzinga-edge-checkout/?t=be8be9we4gewe1be11&utm_source=government-trades"
            rel="noreferrer"
            target="_blank"
          >
            <AiOutlineUnlock size={16} /> Unlock search &amp; filters with Benzinga Edge
          </a>
        )}
      </div>
      {!hideChamber ? (
        <FilterField label="Chamber">
          <select
            disabled={disabled}
            onChange={event => set({ chamber: event.target.value as ToolbarState['chamber'] })}
            value={value.chamber}
          >
            <option value="">All</option>
            <option value="House">House</option>
            <option value="Senate">Senate</option>
          </select>
        </FilterField>
      ) : null}
      <FilterField label="Date">
        <select
          disabled={disabled}
          onChange={event => set({ datePreset: event.target.value })}
          value={value.datePreset}
        >
          {DATE_PRESETS.map(option => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </FilterField>
      {showTransaction ? (
        <FilterField label="Transaction">
          <select
            disabled={disabled}
            onChange={event => set({ transaction: event.target.value as ToolbarState['transaction'] })}
            value={value.transaction}
          >
            <option value="">All Types</option>
            <option value="purchase">Purchase</option>
            <option value="sale">Sale</option>
          </select>
        </FilterField>
      ) : null}
      {!hideSort ? (
        <FilterField label="Sort">
          <select disabled={disabled} onChange={event => set({ sort: event.target.value })} value={value.sort}>
            <option value="">Most Recent</option>
            {SORT_OPTIONS.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </FilterField>
      ) : null}
    </div>
  );
};
